import { useState, useEffect } from 'react'
import { Dropdown, DropdownItem, DropdownDivider, Spinner } from './ui'
import { Badge } from './ui'
import { dataAPI } from '../services/api'

// Provider metadata mapping
const PROVIDER_METADATA = {
  whatsapp: { name: 'WhatsApp', icon: '💬', color: 'green' },
  facebook: { name: 'Facebook', icon: '📘', color: 'blue' },
  instagram: { name: 'Instagram', icon: '📷', color: 'pink' },
  telegram: { name: 'Telegram', icon: '✈️', color: 'cyan' },
  linkedin: { name: 'LinkedIn', icon: '💼', color: 'blue' },
  google: { name: 'Google', icon: '🔍', color: 'red' },
}

const SocialNetworkSwitcher = () => {
  const [providers, setProviders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeNetwork, setActiveNetwork] = useState(() => {
    // Load from localStorage on mount
    return localStorage.getItem('active_social_network') || null
  })

  // Fetch providers from API
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await dataAPI.getAll()
        
        if (response.data.success && response.data.data?.social_providers) {
          const providerIds = response.data.data.social_providers
          
          // Map provider IDs to full provider objects with metadata
          const mappedProviders = providerIds
            .map(id => ({
              id,
              ...(PROVIDER_METADATA[id] || { name: id.charAt(0).toUpperCase() + id.slice(1), icon: '🔗', color: 'gray' })
            }))
            .filter(Boolean) // Remove any undefined entries
          
          setProviders(mappedProviders)
          
          // Set default active network if not set and providers exist
          const savedNetwork = localStorage.getItem('active_social_network')
          if (!savedNetwork && mappedProviders.length > 0) {
            const defaultProvider = mappedProviders[0].id
            setActiveNetwork(defaultProvider)
            localStorage.setItem('active_social_network', defaultProvider)
          } else if (savedNetwork && mappedProviders.find(p => p.id === savedNetwork)) {
            // Validate saved network exists in fetched providers
            setActiveNetwork(savedNetwork)
          } else if (mappedProviders.length > 0) {
            // If saved network doesn't exist, use first available
            setActiveNetwork(mappedProviders[0].id)
          }
        }
      } catch (err) {
        console.error('Failed to fetch social providers:', err)
        setError('Failed to load providers')
        // Fallback to default providers if API fails
        const fallbackProviders = Object.keys(PROVIDER_METADATA).map(id => ({
          id,
          ...PROVIDER_METADATA[id]
        }))
        setProviders(fallbackProviders)
        const savedNetwork = localStorage.getItem('active_social_network')
        if (savedNetwork && fallbackProviders.find(p => p.id === savedNetwork)) {
          setActiveNetwork(savedNetwork)
        } else if (fallbackProviders.length > 0) {
          setActiveNetwork(fallbackProviders[0].id)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchProviders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  useEffect(() => {
    // Save to localStorage whenever active network changes
    if (activeNetwork) {
      localStorage.setItem('active_social_network', activeNetwork)
    }
  }, [activeNetwork])

  const handleNetworkChange = (networkId) => {
    // Save to localStorage
    localStorage.setItem('active_social_network', networkId)
    setActiveNetwork(networkId)
    
    // Reload the entire page after a short delay to ensure state is saved
    setTimeout(() => {
      window.location.reload()
    }, 100)
  }

  const currentNetwork = providers.find(n => n.id === activeNetwork) || providers[0] || null

  // Show loading state
  if (loading) {
    return (
      <button className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 opacity-50 cursor-not-allowed">
        <Spinner size="sm" />
        <span className="text-sm font-medium text-gray-700 hidden sm:inline">Loading...</span>
      </button>
    )
  }

  // Show error or empty state
  if (!currentNetwork || providers.length === 0) {
    return (
      <button className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 opacity-50 cursor-not-allowed">
        <span className="text-sm font-medium text-gray-700">No Networks</span>
      </button>
    )
  }

  return (
    <Dropdown
      trigger={
        <button className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors border border-gray-300">
          <span className="text-lg">{currentNetwork.icon}</span>
          <span className="text-sm font-medium text-gray-700 hidden sm:inline">
            {currentNetwork.name}
          </span>
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      }
      align="right"
    >
      <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        Switch Network
      </div>
      <DropdownDivider />
      {providers.map((network) => (
        <DropdownItem
          key={network.id}
          onClick={() => handleNetworkChange(network.id)}
          className={activeNetwork === network.id ? 'bg-blue-50' : ''}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-base">{network.icon}</span>
              <span className="text-sm">{network.name}</span>
            </div>
            {activeNetwork === network.id && (
              <Badge variant="primary" size="sm">Active</Badge>
            )}
          </div>
        </DropdownItem>
      ))}
    </Dropdown>
  )
}

export default SocialNetworkSwitcher

